import { Temporal } from '@js-temporal/polyfill';
import { useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useAppContext } from 'src/contexts/AppContext';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { useCatalogMultiSelectionContext } from 'src/contexts/CatalogMultiSelectionContext';
import { useFilterQueryContext } from 'src/contexts/FilterQueryContext';
import {
  NO_GROUP_ID,
  useMultiSelectionContext,
} from 'src/contexts/MultiSelectionContext';
import { unpackNumericIds } from 'src/contexts/MultiSelectionContext/MultiSelectionContext.utils';
import { usePurchaseDataContext } from 'src/contexts/PurchaseDataContext';
import { PURCHASE_SINGLE_TIME_PERIOD_QUERY_KEY } from 'src/contexts/PurchaseDataContext/PurchaseDataContext.constants';
import { useSearchDataContextWithCatalogFallback } from 'src/contexts/SearchDataContext';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  ActionOutboxEntityType,
  DateTimeRangeWithRelative,
  EventWithData,
  Feature,
  InventoryViewMode,
  ListingQuery,
  PurchaseClient,
  PurchaseOrderQuery,
  PurchaseViewMode,
  SaleQuery,
  TimePeriodType,
} from 'src/WebApiController';

// Backend buckets weeks via DATEADD(WEEK, DATEDIFF(WEEK, 0, PurchaseOrderDate), 0).
// The Monday-keyed bucket actually spans Sun (key-1d) 00:00 → Sat (key+5d) 23:59:59.999 in UTC.
const weeklyBucketStart = (
  bucketKey: Temporal.PlainDateTime
): Temporal.PlainDateTime =>
  bucketKey
    .subtract({ days: 1 })
    .with({ hour: 0, minute: 0, second: 0, millisecond: 0 });

const lastOfTimePeriod = (
  firstOfPeriod: Temporal.PlainDateTime,
  timePeriodType: Exclude<TimePeriodType, TimePeriodType.Daily>
): Temporal.PlainDateTime => {
  if (timePeriodType === TimePeriodType.Weekly) {
    return firstOfPeriod
      .add({ days: 5 })
      .with({ hour: 23, minute: 59, second: 59, millisecond: 999 });
  }
  return firstOfPeriod.with({
    day: firstOfPeriod.daysInMonth,
    hour: 23,
    minute: 59,
    second: 59,
    millisecond: 999,
  });
};

const firstOfTimePeriod = (
  bucketKey: Temporal.PlainDateTime,
  timePeriodType: Exclude<TimePeriodType, TimePeriodType.Daily>
): Temporal.PlainDateTime =>
  timePeriodType === TimePeriodType.Weekly
    ? weeklyBucketStart(bucketKey)
    : bucketKey;

const intersectWithExisting = (
  rangeStart: string,
  rangeEnd: string,
  existing: DateTimeRangeWithRelative | null
): DateTimeRangeWithRelative => {
  const existingStart = existing?.start ?? null;
  const existingEnd = existing?.end ?? null;
  // Drop relative* fields — backend prioritizes them over start/end.
  return {
    start:
      existingStart && existingStart > rangeStart ? existingStart : rangeStart,
    end: existingEnd && existingEnd < rangeEnd ? existingEnd : rangeEnd,
  } as DateTimeRangeWithRelative;
};

const singleBucketDateRange = (
  bucketId: string,
  timePeriodType: Exclude<TimePeriodType, TimePeriodType.Daily>,
  existing: DateTimeRangeWithRelative | null
): DateTimeRangeWithRelative => {
  const key = Temporal.PlainDateTime.from(bucketId);
  const first = firstOfTimePeriod(key, timePeriodType);
  const last = lastOfTimePeriod(key, timePeriodType);
  return intersectWithExisting(first.toString(), last.toString(), existing);
};

const computeBucketDateRange = (
  bucketGroupIds: string[],
  timePeriodType: Exclude<TimePeriodType, TimePeriodType.Daily>,
  existing: DateTimeRangeWithRelative | null
): DateTimeRangeWithRelative => {
  const ranges = bucketGroupIds.map((bucketKey) => {
    const key = Temporal.PlainDateTime.from(bucketKey);
    return {
      start: firstOfTimePeriod(key, timePeriodType).toString(),
      end: lastOfTimePeriod(key, timePeriodType).toString(),
    };
  });
  const spanStart = ranges.reduce(
    (m, r) => (r.start < m ? r.start : m),
    ranges[0].start
  );
  const spanEnd = ranges.reduce(
    (m, r) => (r.end > m ? r.end : m),
    ranges[0].end
  );
  return intersectWithExisting(spanStart, spanEnd, existing);
};

const sortEvents = (a: EventWithData, b: EventWithData) => {
  const aStart = a.event.dates.start
    ? new Date(a.event.dates.start)
    : new Date();
  const bStart = b.event.dates.start
    ? new Date(b.event.dates.start)
    : new Date();
  return aStart < bStart ? -1 : aStart === bStart ? 0 : 1;
};

export type FilterQueryType = ListingQuery | SaleQuery | PurchaseOrderQuery;

export type UseFilterQueryStateType<T extends FilterQueryType> = T & {
  eventOrMappingIds?: string[] | null;
  purchaseMonths?: string[];
};

type UseFilterQueryState<T extends FilterQueryType> = {
  filterQueryWithEventIds: UseFilterQueryStateType<T>;
  /**
   * Returns a list of selected viagogoIds grouped by event
   */
  selectedViagogoVirtualIds: string[] | undefined;
};

export const useFilterQueryState = <T extends FilterQueryType>(
  entityType:
    | ActionOutboxEntityType.Listing
    | ActionOutboxEntityType.Sale
    | ActionOutboxEntityType.Purchase,
  // All the group ids cannot be outside of this one
  groupIdSuperSet?: string[],
  useTicketGroupIds?: boolean
): UseFilterQueryState<T> => {
  const { flattenedIds, unfilteredFlattenedIds } =
    useCatalogMultiSelectionContext();
  const { getSelection, isAllSelected, isServerSidePagination } =
    useMultiSelectionContext();
  const selectionState = getSelection();

  const { filterQuery } = useFilterQueryContext<T>();
  const { eventsTransformed } = useSearchDataContextWithCatalogFallback();
  const { timePeriodPurchasesQuery, purchaseGroupTimePeriod } =
    usePurchaseDataContext();
  const { activeAccountWebClientConfig } = useAppContext();
  const { allEventIds } = useCatalogDataContext();
  const hasBulkSetPurchaseOrderCostFeature = useUserHasFeature(
    Feature.BulkSetPurchaseOrderCost
  );

  const bucketGroupIds = useMemo(
    () =>
      entityType === ActionOutboxEntityType.Purchase &&
      hasBulkSetPurchaseOrderCostFeature &&
      filterQuery.viewMode === PurchaseViewMode.TileView
        ? (!groupIdSuperSet?.length
            ? selectionState.groupIds
            : selectionState.groupIds.filter((id) =>
                groupIdSuperSet.includes(id)
              )
          ).filter((id) => id !== NO_GROUP_ID)
        : [],
    [
      entityType,
      hasBulkSetPurchaseOrderCostFeature,
      filterQuery.viewMode,
      selectionState.groupIds,
      groupIdSuperSet,
    ]
  );

  const bucketQueries = useQueries({
    queries: bucketGroupIds.map((bucketId) => {
      const bucketRange = singleBucketDateRange(
        bucketId,
        purchaseGroupTimePeriod,
        (filterQuery as PurchaseOrderQuery).purchaseDates ?? null
      );
      const moddedFilterQuery: PurchaseOrderQuery = {
        ...(filterQuery as PurchaseOrderQuery),
        purchaseDates: bucketRange,
      };
      return {
        queryKey: [
          PURCHASE_SINGLE_TIME_PERIOD_QUERY_KEY,
          true,
          moddedFilterQuery,
          allEventIds,
        ],
        queryFn: async () => {
          if (activeAccountWebClientConfig.activeAccountId == null) {
            return null;
          }
          if (allEventIds?.length === 0) {
            return { items: [], summary: null };
          }
          const filterQueryWithEvents: PurchaseOrderQuery = {
            ...moddedFilterQuery,
            eventOrMappingIds: (allEventIds ?? []).map((ev) => ev.viagVirtId),
            performerIds: (allEventIds ?? [])
              .filter((ev) => ev.performerId != null)
              .map((ev) => ev.performerId!),
            venueIds: (allEventIds ?? []).map((ev) => ev.venueId),
          };
          return tryInvokeApi(async () => {
            const client = new PurchaseClient(activeAccountWebClientConfig);
            return await client.getPurchaseOrdersWithTicketGroups(
              filterQueryWithEvents
            );
          });
        },
        enabled: activeAccountWebClientConfig.activeAccountId != null,
        staleTime: Infinity,
        refetchOnWindowFocus: false,
        networkMode: 'offlineFirst' as const,
      };
    }),
  });

  const bucketCascadedPOIds = useMemo(() => {
    if (bucketGroupIds.length === 0) {
      return null;
    }
    if (bucketQueries.some((q) => !q.data)) {
      return null;
    }
    const ids = new Set<number>();
    bucketQueries.forEach((q) => {
      q.data?.items?.forEach((po) => {
        if (po.id != null) {
          ids.add(po.id);
        }
      });
    });
    return Array.from(ids);
  }, [bucketGroupIds, bucketQueries]);

  const isFlattenedViewWithNoGroupId = useMemo(() => {
    const { groupIds, items } = selectionState;
    return (
      filterQuery.viewMode === InventoryViewMode.FlattenedView &&
      ((groupIds.length === 1 && groupIds[0] === NO_GROUP_ID) ||
        (items.groupIds.length === 1 && items.groupIds[0] === NO_GROUP_ID))
    );
  }, [filterQuery.viewMode, selectionState]);

  const filterQueryWithEventIds = useMemo<UseFilterQueryStateType<T>>(() => {
    const { groupIds, items } = selectionState;

    // Clean up the oldPosEventIds for filtering, which is only used for the event inventory page
    if (entityType === ActionOutboxEntityType.Listing) {
      Object.assign(filterQuery as ListingQuery, { oldPosEventIds: null });
    }

    // Flattened view, have to pick events from listings
    if (isFlattenedViewWithNoGroupId) {
      if (
        isAllSelected &&
        flattenedIds?.length === unfilteredFlattenedIds?.length &&
        !isServerSidePagination // SUPER IMPORTANT: If isServerSidePagination is true, we explicitly fill in entityIds into the filter query
      ) {
        const allEventMappingIds =
          eventsTransformed
            ?.map((ev) => ev.event.virtId)
            ?.filter(
              (id) =>
                id !== NO_GROUP_ID &&
                (!groupIdSuperSet?.length || groupIdSuperSet.includes(id))
            ) ?? [];

        if (entityType === ActionOutboxEntityType.Purchase) {
          return {
            ...filterQuery,
            purchaseMonths: timePeriodPurchasesQuery.data?.map(
              (m) => m.firstOfTimePeriod
            ),
            eventOrMappingIds: allEventMappingIds,
          };
        }

        return {
          ...filterQuery,
          eventOrMappingIds: allEventMappingIds,
        };
      } else {
        // Item selections only
        const entityIds = selectionState.items.itemIds;
        return {
          ...filterQuery,
          eventOrMappingIds: [],
          entityIds: entityIds,
        };
      }
    }

    // Non-flattened view

    // Filter the group ids to the super-set so we don't accidentally include more items than this was meant for
    const filteredGroupIds = !groupIdSuperSet?.length
      ? groupIds
      : groupIds.filter((id) => groupIdSuperSet.includes(id));

    if (entityType === ActionOutboxEntityType.Purchase) {
      const unpackedPurchaseIds = new Set<number>();
      const unpackedTicketGroupIds = new Set<number>();
      if (useTicketGroupIds) {
        items.itemIds.forEach((packedId) => {
          const unpackedIds = unpackNumericIds(packedId);
          if (unpackedIds[0]) {
            unpackedPurchaseIds.add(unpackedIds[0]);
          }
          if (unpackedIds[1]) {
            unpackedTicketGroupIds.add(unpackedIds[1]);
          }
        });
      }

      const entityIds: number[] | null = useTicketGroupIds
        ? Array.from(unpackedPurchaseIds)
        : items.itemIds.map((id) => parseInt(id, 10));

      const ticketGroupIds: number[] | null = useTicketGroupIds
        ? Array.from(unpackedTicketGroupIds)
        : null;

      const allEventMappingIds =
        eventsTransformed
          ?.map((ev) => ev.event.virtId)
          ?.filter(
            (id) =>
              id !== NO_GROUP_ID &&
              (!groupIdSuperSet?.length ||
                !useTicketGroupIds ||
                groupIdSuperSet.includes(id))
          ) ?? [];

      const isTimeViewWithBucketSelection =
        hasBulkSetPurchaseOrderCostFeature &&
        filterQuery.viewMode === PurchaseViewMode.TileView &&
        filteredGroupIds.length > 0;

      if (isTimeViewWithBucketSelection && bucketCascadedPOIds != null) {
        const mergedEntityIds = Array.from(
          new Set<number>([...bucketCascadedPOIds, ...(entityIds ?? [])])
        );
        return {
          ...filterQuery,
          purchaseDates: filterQuery.purchaseDates,
          purchaseMonths: undefined,
          eventOrMappingIds: [],
          entityIds: mergedEntityIds,
          ticketGroupIds: ticketGroupIds,
        };
      }

      const bucketPurchaseDates: DateTimeRangeWithRelative | undefined =
        isTimeViewWithBucketSelection
          ? computeBucketDateRange(
              filteredGroupIds,
              purchaseGroupTimePeriod,
              filterQuery.purchaseDates ?? null
            )
          : undefined;

      return {
        ...filterQuery,
        purchaseDates: bucketPurchaseDates ?? filterQuery.purchaseDates,
        // If it's a purchase query, use the purchase months
        purchaseMonths: isTimeViewWithBucketSelection
          ? undefined
          : useTicketGroupIds
            ? undefined
            : filteredGroupIds,
        eventOrMappingIds: isTimeViewWithBucketSelection
          ? allEventMappingIds
          : useTicketGroupIds
            ? filteredGroupIds
            : allEventMappingIds,
        entityIds: entityIds,
        ticketGroupIds: ticketGroupIds,
      };
    }

    if (eventsTransformed == null) {
      return {
        ...filterQuery,
        eventOrMappingIds: filteredGroupIds,
        entityIds: items.itemIds.map((id) => parseInt(id, 10)),
      };
    }

    // We want to make sure we get all the events sharing the same group ids because a single gropu id can be part of a merged event
    const normalizedGroupIdsByMergedEvents = eventsTransformed
      ?.filter((ev) => groupIds.includes(ev.event.virtId))
      ?.map((ev) => ev.event.virtId)
      ?.filter((id) => id.length && !id.includes(NO_GROUP_ID));

    return {
      ...filterQuery,
      // Get all the event ids that matches the viagogo ids in groupIdsAsViagogoIds
      eventOrMappingIds: normalizedGroupIdsByMergedEvents,
      entityIds: items.itemIds.map((id) => parseInt(id, 10)),
    };
  }, [
    selectionState,
    entityType,
    isFlattenedViewWithNoGroupId,
    groupIdSuperSet,
    eventsTransformed,
    filterQuery,
    isAllSelected,
    flattenedIds?.length,
    unfilteredFlattenedIds?.length,
    timePeriodPurchasesQuery.data,
    useTicketGroupIds,
    hasBulkSetPurchaseOrderCostFeature,
    purchaseGroupTimePeriod,
    bucketCascadedPOIds,
  ]);

  // This is used for the Catchup
  const selectedViagogoVirtualIds = useMemo<string[] | undefined>(() => {
    const { groupIds, items } = selectionState;

    // Flattened view, have to pick events from listings
    if (isFlattenedViewWithNoGroupId) {
      if (isAllSelected) {
        return eventsTransformed
          ?.toSorted(sortEvents)
          ?.map((ev) => ev.event.virtId);
      } else {
        const entityIds = items.itemIds;
        return eventsTransformed
          ?.filter((ev) =>
            entityType === ActionOutboxEntityType.Listing
              ? ev.entities.listings?.some((l) =>
                  entityIds.includes(l.id.toString())
                )
              : ev.entities.sales?.some((s) =>
                  entityIds.includes(s.id.toString())
                )
          )
          ?.toSorted(sortEvents)
          ?.map((ev) => ev.event.virtId)
          ?.filter((id) => id.length && !id.includes(NO_GROUP_ID));
      }
    }

    // If the selection has events, use it
    if (groupIds?.length) {
      if (entityType === ActionOutboxEntityType.Purchase) {
        return;
      }

      return eventsTransformed
        ?.filter((ev) => groupIds.includes(ev.event.virtId))
        ?.toSorted(sortEvents)
        ?.map((ev) => ev.event.virtId)
        ?.filter((id) => id.length && !id.includes(NO_GROUP_ID));
    }

    return;
  }, [
    entityType,
    eventsTransformed,
    isAllSelected,
    isFlattenedViewWithNoGroupId,
    selectionState,
  ]);

  return { filterQueryWithEventIds, selectedViagogoVirtualIds };
};
